import React, { useState } from 'react';
import { TextField, Button, Container, Typography, Box, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../store/slices/authSlice';
import { login } from '../utils/api';

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleLogin = async () => {
        if (username === '' || password === '') {
            setError('Username and password are required');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await login(
                { username, password },
                { message: `User ${username} logged in to the panel.` }
            );
            const { user, token } = response.data;

            // Store token in localStorage
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            // Persist userId separately for audit fallbacks
            const resolvedUserId =
                user?.userId ??
                user?.id ??
                user?.user?.userId ??
                user?.user?.id ??
                user?.appUserId ??
                null;

            if (resolvedUserId !== null && resolvedUserId !== undefined) {
                localStorage.setItem('userId', resolvedUserId.toString());
            } else {
                localStorage.removeItem('userId');
            }

            // Get userName with fallback logic matching getCurrentUserName
            const firstName = user?.firstName ?? user?.user?.firstName ?? '';
            const lastName = user?.lastName ?? user?.user?.lastName ?? '';
            const userUsername = user?.username ?? user?.user?.username ?? '';
            const employeeCode = user?.employeeCode ?? user?.user?.employeeCode ?? '';
            
            const fullName = `${firstName} ${lastName}`.trim();
            const userName = fullName || userUsername || employeeCode || null;
            
            if (userName) {
                localStorage.setItem('userName', userName);
            } else {
                localStorage.removeItem('userName');
            }

            // Store user privileges and group name in localStorage for permission checks
            if (user.userGroup) {
                // Store group name for super-admin check
                localStorage.setItem('userGroupName', user.userGroup.groupName || '');
                
                // Store privileges
                if (user.userGroup.privileges) {
                    const privileges = typeof user.userGroup.privileges === 'string' 
                        ? user.userGroup.privileges 
                        : JSON.stringify(user.userGroup.privileges);
                    localStorage.setItem('userPrivileges', privileges);
                }
            }

            // Update Redux store with user data and token
            dispatch(setCredentials({ user, token }));

            // Navigate to dashboard
            navigate('/dashboard');
        } catch (error) {
            console.error('Login error:', error);
            setError(error.response?.data?.message || 'Invalid username or password');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="sm">
            <Box display="flex" flexDirection="column" alignItems="center" mt={8}>
                <Typography variant="h4" gutterBottom>
                    Login
                </Typography>
                {error && (
                    <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <TextField
                    label="Username"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                />
                <TextField
                    label="Password"
                    type="password"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLogin}
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={isLoading}
                >
                    {isLoading ? <CircularProgress size={24} /> : 'Login'}
                </Button>
            </Box>
        </Container>
    );
};

export default LoginPage;